import { createApi } from '@reduxjs/toolkit/query/react';

import { getData } from '../../services/API';
import { tasksByIdUrl } from '../../services/API/endpoints';
import { processError } from './utils/transformations';

import { cleanIssue, cleanFeedback } from './utils/cleaners';

/**
 * RTK Query API slice for task features.
 *
 * @remarks
 * Provides endpoints for:
 * - Issue by id (`getIssueById`)
 * - Feedback by id (`getFeedbackById`)
 * - Other config-related endpoints as defined
 *
 * @example
 * import { useGetIssueByIdQuery } from 'src/redux/apis/Tasks.api';
 * const { data } = useGetIssueByIdQuery();
 */
export const tasksApi = createApi({
  reducerPath: 'tasksApi',
  baseQuery: (operation: any) => operation,
  tagTypes: ['issue-by-id', 'feedback-by-id'],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    getIssueById: builder.query({
      query: (issueId: string) =>
        getData(`${tasksByIdUrl}?taskId[]=${issueId}`, {}),
      transformResponse: response => cleanIssue(response[0]),
      transformErrorResponse: processError,
      providesTags: (_result, _error, issueId) => [
        {
          type: 'issue-by-id',
          id: issueId
        }
      ]
    }),
    getFeedbackById: builder.query({
      query: (feedbackId: string) =>
        getData(`${tasksByIdUrl}?taskId[]=${feedbackId}`, {}),
      transformResponse: response => cleanFeedback(response[0]),
      transformErrorResponse: processError,
      providesTags: (_result, _error, feedbackId) => [
        {
          type: 'feedback-by-id',
          id: feedbackId
        }
      ]
    })
  })
});

export const {
  useGetIssueByIdQuery,
  useLazyGetIssueByIdQuery,
  useGetFeedbackByIdQuery,
  useLazyGetFeedbackByIdQuery
} = tasksApi;
